// Roles disponibles en la plataforma Domus
export const USER_ROLES = {
    PENDING_APPROVAL: 'pending_approval',
    USER: 'user',
    ADMIN: 'admin',
    SUPERADMIN: 'superadmin'
}

// Jerarquía de roles (de menor a mayor privilegio)
export const ROLE_HIERARCHY = {
    [USER_ROLES.PENDING_APPROVAL]: 0,
    [USER_ROLES.USER]: 1,
    [USER_ROLES.ADMIN]: 2,
    [USER_ROLES.SUPERADMIN]: 3
}

// Permisos por rol
export const ROLE_PERMISSIONS = {
    [USER_ROLES.PENDING_APPROVAL]: {
        canAccessDashboard: false,
        canManageDevices: false,
        canViewUsers: false,
        canManageUsers: false,
        canViewReports: false,
        canAccessSettings: false
    },
    [USER_ROLES.USER]: {
        canAccessDashboard: true,
        canManageDevices: true,
        canViewUsers: false,
        canManageUsers: false,
        canViewReports: true,
        canAccessSettings: true
    },
    [USER_ROLES.ADMIN]: {
        canAccessDashboard: true,
        canManageDevices: true,
        canViewUsers: true,
        canManageUsers: true,
        canViewReports: true,
        canAccessSettings: true,
        canManageGroupUsers: true
    },
    [USER_ROLES.SUPERADMIN]: {
        canAccessDashboard: true,
        canManageDevices: true,
        canViewUsers: true,
        canManageUsers: true,
        canViewReports: true,
        canAccessSettings: true,
        canManageGroupUsers: true,
        canManageAllUsers: true,
        canAccessSystemSettings: true
    }
}

/**
 * Verifica si un usuario tiene un rol específico
 */
export function hasRole(user, role) {
    if (!user || !user.role) return false
    return user.role === role
}

/**
 * Verifica si un usuario tiene al menos el nivel de rol especificado
 */
export function hasMinimumRole(user, minimumRole) {
    if (!user || !user.role) return false

    const userLevel = ROLE_HIERARCHY[user.role]
    const requiredLevel = ROLE_HIERARCHY[minimumRole]

    return userLevel >= requiredLevel
}

/**
 * Verifica si un usuario tiene un permiso específico
 */
export function hasPermission(user, permission) {
    if (!user || !user.role) return false

    const rolePermissions = ROLE_PERMISSIONS[user.role]
    return rolePermissions && rolePermissions[permission] === true
}

/**
 * Obtiene todos los permisos de un usuario
 */
export function getUserPermissions(user) {
    if (!user || !user.role) return {}

    return ROLE_PERMISSIONS[user.role] || {}
}

/**
 * Verifica si un usuario puede acceder a una ruta específica
 */
export function canAccessRoute(user, route) {
    if (!user) return false

    // Los usuarios pendientes de aprobación no pueden acceder a nada excepto a la página de espera
    if (hasRole(user, USER_ROLES.PENDING_APPROVAL)) {
        return route === '/pending-approval'
    }

    // Rutas públicas (después de autenticación)
    const publicRoutes = ['/dashboard', '/profile', '/settings']
    if (publicRoutes.includes(route) && hasMinimumRole(user, USER_ROLES.USER)) {
        return true
    }

    // Rutas de administración
    const adminRoutes = ['/admin', '/users', '/user-management']
    if (adminRoutes.includes(route) && hasMinimumRole(user, USER_ROLES.ADMIN)) {
        return true
    }

    // Rutas de superadministrador
    const superAdminRoutes = ['/system-settings', '/global-admin']
    if (superAdminRoutes.includes(route) && hasRole(user, USER_ROLES.SUPERADMIN)) {
        return true
    }

    return false
}

/**
 * Obtiene el nombre legible del rol
 */
export function getRoleName(role) {
    const roleNames = {
        [USER_ROLES.PENDING_APPROVAL]: 'Pendiente de Aprobación',
        [USER_ROLES.USER]: 'Usuario',
        [USER_ROLES.ADMIN]: 'Administrador',
        [USER_ROLES.SUPERADMIN]: 'Superadministrador'
    }

    return roleNames[role] || 'Rol Desconocido'
}

/**
 * Obtiene el color del badge para un rol
 */
export function getRoleColor(role) {
    const roleColors = {
        [USER_ROLES.PENDING_APPROVAL]: 'bg-yellow-100 text-yellow-800',
        [USER_ROLES.USER]: 'bg-blue-100 text-blue-800',
        [USER_ROLES.ADMIN]: 'bg-green-100 text-green-800',
        [USER_ROLES.SUPERADMIN]: 'bg-purple-100 text-purple-800'
    }

    return roleColors[role] || 'bg-gray-100 text-gray-800'
}